import { create } from '@bufbuild/protobuf';
import { createClient } from '@connectrpc/connect';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import {
  CreateWorkspaceRequestSchema,
  DeleteMode,
  DeleteWorkspaceRequestSchema,
  DeployBaisRequestSchema,
  DeployFromMainRequestSchema,
  DeployMaiRequestSchema,
  GetAdHocPreflightStatusRequestSchema,
  GetLastWorkspaceWorkflowRunRequestSchema,
  GetWorkspacePreflightStatusRequestSchema,
  GetWorkspaceRequestSchema,
  ListAccessibleWorkspacesRequestSchema,
  ListAvailableReleasesRequestSchema,
  ListWorkspacesRequestSchema,
  PreflightRunStatus,
  PreviewReleaseDiffRequestSchema,
  PromoteReleaseMultiRequestSchema,
  PromoteReleaseRequestSchema,
  ReleaseDiffStatus,
  RetryWorkspaceProvisioningRequestSchema,
  RunAdHocPreflightRequestSchema,
  RunReleasePlanRequestSchema,
  RunWorkspacePreflightRequestSchema,
  StreamWorkspaceEventsRequestSchema,
  UpdateWorkspaceClusterNameRequestSchema,
  WorkspaceEnvironment,
  WorkspaceService,
  WorkspaceStatus,
} from '../gen/deployments/v1/deployments_pb';
import { createApiTransport, createAuthApiTransport } from '../transport';
import { isNonRetriable, streamSleep } from './streamingUtils';
import type { AccessRuleInputData } from './useUserService';

export { PreflightRunStatus, ReleaseDiffStatus, WorkspaceEnvironment, WorkspaceStatus };

function transport(token?: string | null) {
  return token ? createAuthApiTransport(token) : createApiTransport();
}

export interface ListWorkspacesParams {
  search?: string;
  page?: number;
  pageSize?: number;
  environmentFilter?: WorkspaceEnvironment;
  statusFilter?: WorkspaceStatus;
  releaseIdFilter?: string;
  /** Only applies when deploymentId is omitted/empty — narrows the global list to exactly this tenant. */
  tenantId?: string;
}

/**
 * Hook to list workspaces with optional server-side search and pagination.
 *
 * @param deploymentId - The deployment ID to filter workspaces by. If omitted or empty, returns all workspaces globally.
 * @param token - Auth0 access token (required for authenticated endpoints)
 * @param params - Optional search and pagination params
 * @returns Query result with list of workspaces and totalCount
 */
export function useListWorkspaces(
  deploymentId?: string | null,
  token?: string | null,
  params?: ListWorkspacesParams,
) {
  return useQuery({
    queryKey: [
      'workspaces',
      'list',
      deploymentId ?? '',
      !!token,
      params?.search ?? '',
      params?.page ?? 1,
      params?.pageSize ?? 20,
      params?.environmentFilter ?? WorkspaceEnvironment.UNSPECIFIED,
      params?.statusFilter ?? WorkspaceStatus.UNSPECIFIED,
      params?.releaseIdFilter ?? '',
      params?.tenantId ?? '',
    ],
    queryFn: async () => {
      const client = createClient(WorkspaceService, transport(token));
      const request = create(ListWorkspacesRequestSchema, {
        deploymentId: deploymentId ?? '',
        search: params?.search ?? '',
        page: params?.page ?? 1,
        pageSize: params?.pageSize ?? 20,
        environmentFilter: params?.environmentFilter ?? WorkspaceEnvironment.UNSPECIFIED,
        statusFilter: params?.statusFilter ?? WorkspaceStatus.UNSPECIFIED,
        releaseIdFilter: params?.releaseIdFilter ?? '',
        tenantId: params?.tenantId ?? '',
      });
      return await client.listWorkspaces(request);
    },
    enabled: !!token,
  });
}

export interface ListAccessibleWorkspacesParams {
  search?: string;
  page?: number;
  pageSize?: number;
  roleRules?: AccessRuleInputData[];
  userRules?: AccessRuleInputData[];
}

/**
 * Hook to list workspaces visible under a candidate (not persisted)
 * access-rule scope passed in the request itself — ListAccessibleDeployments'
 * workspace counterpart. roleRules/userRules may contain both deployment-kind
 * and workspace-kind rows; a deployment-kind row gates a workspace by its
 * parent deployment even though this call never names a deploymentId.
 *
 * @param token - Auth0 access token (required for authenticated endpoints)
 * @param tenantId - Filter by direct tenant ID. Mutually exclusive with parentTenantId.
 * @param parentTenantId - Filter by parent tenant ID — a partner tenant owns no deployments/workspaces directly, so scoping to a partner needs this instead of tenantId.
 * @param params - Search/pagination and the candidate rule envelopes
 * @param enabled - Set false to skip fetching (e.g. while the condition being previewed is incomplete)
 * @returns Query result with list of workspaces and totalCount
 */
export function useListAccessibleWorkspaces(
  token?: string | null,
  tenantId?: string,
  parentTenantId?: string,
  params?: ListAccessibleWorkspacesParams,
  enabled = true,
) {
  return useQuery({
    queryKey: [
      'workspaces',
      'accessible',
      tenantId ?? '',
      parentTenantId ?? '',
      !!token,
      params?.search ?? '',
      params?.page ?? 1,
      params?.pageSize ?? 20,
      params?.roleRules ?? [],
      params?.userRules ?? [],
    ],
    queryFn: async () => {
      const client = createClient(WorkspaceService, transport(token));
      const request = create(ListAccessibleWorkspacesRequestSchema, {
        tenantId: tenantId ?? '',
        parentTenantId: parentTenantId ?? '',
        search: params?.search ?? '',
        page: params?.page ?? 1,
        pageSize: params?.pageSize ?? 20,
        roleRules: (params?.roleRules ?? []).map((r) => ({ ...r, effect: r.effect ?? 'allow' })),
        userRules: (params?.userRules ?? []).map((r) => ({ ...r, effect: r.effect ?? 'allow' })),
      });
      return await client.listAccessibleWorkspaces(request);
    },
    enabled: !!token && enabled,
  });
}

/**
 * Hook to retrieve a single workspace by ID.
 *
 * @param id - The workspace ID to retrieve
 * @param token - Auth0 access token (required for authenticated endpoints)
 * @returns Query result with workspace data including infrastructure and upgrade status
 */
export function useGetWorkspace(id: string, token?: string | null) {
  return useQuery({
    queryKey: ['workspaces', id, !!token],
    queryFn: async () => {
      const client = createClient(WorkspaceService, transport(token));
      const request = create(GetWorkspaceRequestSchema, { id });
      return await client.getWorkspace(request);
    },
    enabled: !!id && !!token,
  });
}

/**
 * Mutation hook to set the Kubernetes cluster name on a workspace.
 * On success, invalidates the workspace cache so UI reflects the updated value.
 *
 * @param token - Auth0 access token (required for authenticated endpoints)
 * @returns Mutation result for updating the cluster name
 */
export function useUpdateWorkspaceClusterName(token?: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; clusterName: string }) => {
      const client = createClient(WorkspaceService, transport(token));
      const request = create(UpdateWorkspaceClusterNameRequestSchema, {
        id: input.id,
        clusterName: input.clusterName,
      });
      return await client.updateWorkspaceClusterName(request);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['workspaces', variables.id] });
    },
  });
}

/**
 * Mutation hook to create a new workspace within a deployment.
 * On success, invalidates the workspaces list cache for the deployment.
 *
 * @param token - Auth0 access token (required for authenticated endpoints)
 * @returns Mutation result for creating the workspace
 */
export function useCreateWorkspace(token?: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      deploymentId: string;
      environment: WorkspaceEnvironment;
      displayName: string;
      slug: string;
      releaseId: string;
      infra?: string;
      createInfra?: boolean;
      acknowledgeUnknowns?: boolean;
    }) => {
      const client = createClient(WorkspaceService, transport(token));
      const request = create(CreateWorkspaceRequestSchema, {
        deploymentId: input.deploymentId,
        environment: input.environment,
        displayName: input.displayName,
        slug: input.slug,
        releaseId: input.releaseId,
        infra: input.infra ?? '',
        createInfra: input.createInfra ?? false,
        acknowledgeUnknowns: input.acknowledgeUnknowns ?? false,
      });
      return await client.createWorkspace(request);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['workspaces', 'list', variables.deploymentId] });
    },
  });
}

/**
 * Mutation hook to delete a workspace.
 * Initiates deprovisioning and tombstoning of the workspace.
 * On success, invalidates the workspace list and detail caches.
 *
 * @param token - Auth0 access token (required for authenticated endpoints)
 * @returns Mutation result for deleting a workspace
 */
export function useDeleteWorkspace(token?: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; mode?: DeleteMode }) => {
      const client = createClient(WorkspaceService, transport(token));
      const request = create(DeleteWorkspaceRequestSchema, {
        id: input.id,
        mode: input.mode ?? DeleteMode.FULL,
      });
      return await client.deleteWorkspace(request);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['workspaces', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['workspaces', 'list'] });
    },
  });
}

/**
 * Hook to list non-deprecated releases newer than the workspace's current release.
 *
 * @param workspaceId - The workspace whose current_release_id sets the lower bound
 * @param token - Auth0 access token
 * @param releaseGroup - Optional application group filter (e.g. "baic")
 * @returns Query result with list of available Release records
 */
export function useListAvailableReleases(
  workspaceId: string,
  token?: string | null,
  releaseGroup: string = 'baic',
) {
  return useQuery({
    queryKey: ['workspaces', workspaceId, 'availableReleases', releaseGroup, !!token],
    queryFn: async () => {
      const client = createClient(WorkspaceService, transport(token));
      const request = create(ListAvailableReleasesRequestSchema, {
        workspaceId,
        releaseGroup,
      });
      return await client.listAvailableReleases(request);
    },
    enabled: !!workspaceId && !!token,
  });
}

/**
 * Mutation hook that previews a release change for a workspace, returning
 * the diff directly - no polling hook needed. When releaseId is omitted, it
 * re-syncs the workspace's current major release's manifest via
 * x-forge-installer and blocks until the sync completes (this can
 * legitimately take minutes - the sync clones and inspects every
 * microservice repo - so callers should show a loading state for the
 * mutation's whole lifetime, not just the initial request). When releaseId
 * is a different, already-published release, it resolves near-instantly - a
 * pure in-process diff against that release's stored manifest.
 */
export function usePreviewReleaseDiff(token?: string | null) {
  return useMutation({
    mutationFn: async (input: { workspaceId: string; releaseId?: string }) => {
      const client = createClient(WorkspaceService, transport(token));
      const request = create(PreviewReleaseDiffRequestSchema, {
        workspaceId: input.workspaceId,
        releaseId: input.releaseId ?? '',
      });
      return await client.previewReleaseDiff(request);
    },
  });
}

/**
 * Mutation hook that actually applies a release to a workspace's cluster:
 * dispatches weekly_release_promote.yml via the releasepromote Temporal
 * workflow and returns immediately once it's been kicked off - it does not
 * wait for the promote itself to finish (a full Terraform apply, possibly
 * behind a manual-approval gate, can take hours). Pass releaseId to promote
 * to a different, already-published release; omit it to re-apply a refresh
 * of the current release's just-synced manifest. Progress is observed via
 * the workspace's own upgrade_status field and its audit history, both kept
 * live by useStreamWorkspaceEvents - no polling needed here.
 *
 * @param token - Auth0 access token
 * @returns Mutation result; on success the workspace is refetched with
 *          upgrade_status=IN_PROGRESS and target_release_id populated.
 */
export function usePromoteRelease(token?: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      workspaceId: string;
      releaseId?: string;
      runLiquibase?: boolean;
    }) => {
      const client = createClient(WorkspaceService, transport(token));
      const request = create(PromoteReleaseRequestSchema, {
        workspaceId: input.workspaceId,
        releaseId: input.releaseId ?? '',
        runLiquibase: input.runLiquibase ?? false,
      });
      return await client.promoteRelease(request);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['workspaces', variables.workspaceId] });
      queryClient.invalidateQueries({
        queryKey: ['workspaces', variables.workspaceId, 'availableReleases'],
      });
      queryClient.invalidateQueries({ queryKey: ['workspaces', 'list'] });
    },
  });
}

/**
 * Mutation hook that fans a release promote/upgrade out across up to 5
 * workspaces in one batch: dispatches release_promote_multi.yml via the
 * releasepromotemulti Temporal workflow and returns immediately once it's
 * been kicked off. Unlike usePromoteRelease, releaseId is required — there's
 * no single "refresh whatever's already running" target across multiple
 * workspaces. The server rejects (FailedPrecondition) if the named
 * workspaces don't all share the same current_release_id/deployment, if more
 * than 5 are named, or if any already has an upgrade in progress. Progress is
 * observed per-workspace via each workspace's own upgrade_status and audit
 * history, both kept live by useStreamWorkspaceEvents — no polling needed
 * here.
 *
 * @param token - Auth0 access token
 * @returns Mutation result whose data is PromoteReleaseMultiResponse
 *          (workflow_id, dispatched_at_ms, echoed workspace_ids)
 */
export function usePromoteReleaseMulti(token?: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      workspaceIds: string[];
      releaseId: string;
      runLiquibase?: boolean;
      /** "plan" for a non-destructive Terraform dry-run, "apply" (or
       * omitted) for the real promote/upgrade - see PromoteReleaseMultiRequest's
       * own doc comment; empty defaults to "apply" server-side. */
      terraformOperation?: 'plan' | 'apply';
    }) => {
      const client = createClient(WorkspaceService, transport(token));
      const request = create(PromoteReleaseMultiRequestSchema, {
        workspaceIds: input.workspaceIds,
        releaseId: input.releaseId,
        runLiquibase: input.runLiquibase ?? false,
        terraformOperation: input.terraformOperation ?? '',
      });
      return await client.promoteReleaseMulti(request);
    },
    onSuccess: (data) => {
      for (const workspaceId of data.workspaceIds) {
        queryClient.invalidateQueries({ queryKey: ['workspaces', workspaceId] });
        queryClient.invalidateQueries({
          queryKey: ['workspaces', workspaceId, 'availableReleases'],
        });
      }
      queryClient.invalidateQueries({ queryKey: ['workspaces', 'list'] });
    },
  });
}

/**
 * Mutation hook to dispatch a Terraform dry-run ("Plan") against a workspace's
 * target release - the read-only counterpart to usePromoteRelease. Unlike
 * promote, a successful dispatch changes nothing persisted (no
 * upgrade_status, no release_metadata), so there is deliberately no
 * onSuccess cache invalidation here. Subscribe with
 * useStreamWorkflowRunEvents using the response's workflowId/dispatchedAtMs.
 *
 * @param token - Auth0 access token (required for authenticated endpoints)
 * @returns Mutation result whose data is the RunReleasePlanResponse (deterministic workflow_id + dispatched_at_ms)
 */
export function useRunReleasePlan(token?: string | null) {
  return useMutation({
    mutationFn: async (input: {
      workspaceId: string;
      releaseId?: string;
      runLiquibase?: boolean;
    }) => {
      const client = createClient(WorkspaceService, transport(token));
      const request = create(RunReleasePlanRequestSchema, {
        workspaceId: input.workspaceId,
        releaseId: input.releaseId ?? '',
        runLiquibase: input.runLiquibase ?? false,
      });
      return await client.runReleasePlan(request);
    },
  });
}

/**
 * Mutation hook to dispatch sun_deploy_from_main.yml for a single service off
 * an arbitrary branch, straight to a workspace's live cluster - distinct from
 * PromoteRelease/RunReleasePlan: no release_id, and current_release_id/
 * current_version are never touched. Dispatches and returns immediately;
 * track progress via the workspace's audit history.
 *
 * @param token - Auth0 access token (required for authenticated endpoints)
 * @returns Mutation result whose data is the DeployFromMainResponse (workflow_id + dispatched_at_ms)
 */
export function useDeployFromMain(token?: string | null) {
  return useMutation({
    mutationFn: async (input: {
      workspaceId: string;
      installerBranch?: string;
      serviceName: string;
      serviceBranch: string;
      imageTag?: string;
      chartVersion?: string;
      refreshIngress?: boolean;
      runLiquibase?: boolean;
    }) => {
      const client = createClient(WorkspaceService, transport(token));
      const request = create(DeployFromMainRequestSchema, {
        workspaceId: input.workspaceId,
        installerBranch: input.installerBranch ?? '',
        serviceName: input.serviceName,
        serviceBranch: input.serviceBranch,
        imageTag: input.imageTag ?? '',
        chartVersion: input.chartVersion ?? '',
        refreshIngress: input.refreshIngress ?? false,
        runLiquibase: input.runLiquibase ?? false,
      });
      return await client.deployFromMain(request);
    },
  });
}

/**
 * Mutation hook to dispatch sun_deploy_bais.yml (BAIS/"CSAI") against a
 * workspace. Fire-and-forget - track progress via the workspace's History tab.
 *
 * @param token - Auth0 access token (required for authenticated endpoints)
 * @returns Mutation result whose data is the DeployBaisResponse (workflow_id + dispatched_at_ms)
 */
export function useDeployBais(token?: string | null) {
  return useMutation({
    mutationFn: async (input: { workspaceId: string; releaseId?: string }) => {
      const client = createClient(WorkspaceService, transport(token));
      const request = create(DeployBaisRequestSchema, {
        workspaceId: input.workspaceId,
        releaseId: input.releaseId ?? '',
      });
      return await client.deployBais(request);
    },
  });
}

/**
 * Mutation hook to dispatch deploy_mai_components.yml against a workspace.
 * Fire-and-forget - track progress via the workspace's History tab.
 *
 * @param token - Auth0 access token (required for authenticated endpoints)
 * @returns Mutation result whose data is the DeployMaiResponse (workflow_id + dispatched_at_ms)
 */
export function useDeployMai(token?: string | null) {
  return useMutation({
    mutationFn: async (input: { workspaceId: string; installerBranch?: string }) => {
      const client = createClient(WorkspaceService, transport(token));
      const request = create(DeployMaiRequestSchema, {
        workspaceId: input.workspaceId,
        installerBranch: input.installerBranch ?? '',
      });
      return await client.deployMai(request);
    },
  });
}

/**
 * Fetches the most recent workflow_runs row for a workspace, either the last
 * run of any kind (scope omitted - the page-level "is anything in flight /
 * what last happened" banner) or one scoped to a specific deterministic
 * workflow (currently "release_plan", replacing the old
 * useGetLatestReleasePlanRun). found=false covers both "never run" and
 * "finished too long ago to still show" - the server collapses those on
 * purpose. An in-progress run is always returned regardless of age.
 *
 * staleTime: Infinity - purely event-driven, no time-based refetch.
 * WorkspaceDetail's `useStreamWorkspaceEvents` already invalidates this
 * query's cache key on every workspace/audit/workflow_run event (any
 * non-keepalive message, including the snapshot sent right on connect),
 * which bypasses staleTime and forces a refetch - so a fresh dispatch,
 * completion, or a dropped-then-restored connection all refetch live
 * without ever needing a refetchInterval or refetch-on-focus/mount.
 *
 * @param workspaceId - the workspace to check
 * @param token - Auth0 access token (required for authenticated endpoints)
 * @param scope - "" (default) for any kind, or "release_plan" for the
 *   workspace's release-plan dry run specifically
 * @param enabled - pass false to skip the query (e.g. while its dialog is closed)
 */
export function useGetLastWorkspaceWorkflowRun(
  workspaceId: string,
  token?: string | null,
  scope: string = '',
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: ['workspaces', workspaceId, 'lastWorkflowRun', scope, !!token],
    staleTime: Infinity,
    queryFn: async () => {
      const client = createClient(WorkspaceService, transport(token));
      const request = create(GetLastWorkspaceWorkflowRunRequestSchema, { workspaceId, scope });
      return await client.getLastWorkspaceWorkflowRun(request);
    },
    enabled: enabled && !!workspaceId && !!token,
  });
}

/**
 * Mutation hook to START an async preflight run for a workspace before install.
 * The PreflightWorkflow outlasts the HTTP write timeout, so this returns
 * immediately with { workflowId, status: RUNNING }; poll the result with
 * useGetWorkspacePreflightStatus(deploymentId, slug). It is a mutation because
 * it triggers a workflow start and is explicitly re-runnable.
 *
 * @param token - Auth0 access token (required for authenticated endpoints)
 * @returns Mutation result whose data is the RunWorkspacePreflightResponse (start ack)
 */
export function useRunWorkspacePreflight(token?: string | null) {
  return useMutation({
    mutationFn: async (input: {
      deploymentId: string;
      slug: string;
      releaseId?: string;
      staticOnly?: boolean;
      mode?: string;
      scope?: string;
    }) => {
      const client = createClient(WorkspaceService, transport(token));
      const request = create(RunWorkspacePreflightRequestSchema, {
        deploymentId: input.deploymentId,
        slug: input.slug,
        releaseId: input.releaseId ?? '',
        staticOnly: input.staticOnly ?? false,
        mode: input.mode ?? '',
        scope: input.scope ?? '',
      });
      return await client.runWorkspacePreflight(request);
    },
  });
}

/**
 * Polling query for the async preflight run's status and (once finished) report.
 * Keyed per (deployment, slug) — the same key the start RPC uses. While the run
 * is RUNNING it re-polls every 2s; it stops polling on COMPLETED/FAILED. Pass
 * `enabled` to gate polling (e.g. only while the checklist dialog is open and a
 * run has been started).
 *
 * @param deploymentId - Deployment the workspace belongs to
 * @param slug - Workspace slug (infra_name) the run is keyed on
 * @param token - Auth0 access token
 * @param enabled - When false, the query is idle (no polling)
 */
export function useGetWorkspacePreflightStatus(
  deploymentId: string,
  slug: string,
  token?: string | null,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: ['preflight', deploymentId, slug, !!token],
    queryFn: async () => {
      const client = createClient(WorkspaceService, transport(token));
      const request = create(GetWorkspacePreflightStatusRequestSchema, { deploymentId, slug });
      return await client.getWorkspacePreflightStatus(request);
    },
    enabled: enabled && !!deploymentId && !!token,
    // Re-poll while RUNNING; stop once the run reaches a terminal state.
    refetchInterval: (query) =>
      query.state.data?.status === PreflightRunStatus.RUNNING ? 2000 : false,
  });
}

/**
 * Starts an ad-hoc preflight run against an AWS account with no deployment
 * record. Used for the pre-create readiness gate on the New Deployment page: the
 * operator supplies only the target account id; the server resolves a recorded
 * cross-account role for it and either runs the checks (returning a workflow id
 * to poll) or returns an immediate verdict message (new account / can't assume).
 *
 * @param token - Auth0 access token
 * @returns Mutation result whose data is the RunAdHocPreflightResponse
 */
export function useRunAdHocPreflight(token?: string | null) {
  return useMutation({
    mutationFn: async (input: {
      region: string;
      accountId: string;
      scope?: string;
      staticOnly?: boolean;
    }) => {
      const client = createClient(WorkspaceService, transport(token));
      const request = create(RunAdHocPreflightRequestSchema, {
        region: input.region,
        accountId: input.accountId,
        scope: input.scope ?? '',
        staticOnly: input.staticOnly ?? false,
      });
      return await client.runAdHocPreflight(request);
    },
  });
}

/**
 * Polling query for an ad-hoc preflight run's status and (once finished) report,
 * keyed purely by the workflow id returned from useRunAdHocPreflight (there is no
 * deployment/slug). Re-polls every 2s while RUNNING; stops on COMPLETED/FAILED.
 * Pass `enabled` to gate polling (e.g. only while the checklist dialog is open).
 *
 * @param workflowId - The workflow id returned by RunAdHocPreflight
 * @param token - Auth0 access token
 * @param enabled - When false, the query is idle (no polling)
 */
export function useGetAdHocPreflightStatus(
  workflowId: string,
  token?: string | null,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: ['preflight-adhoc', workflowId, !!token],
    queryFn: async () => {
      const client = createClient(WorkspaceService, transport(token));
      const request = create(GetAdHocPreflightStatusRequestSchema, { workflowId });
      return await client.getAdHocPreflightStatus(request);
    },
    enabled: enabled && !!workflowId && !!token,
    refetchInterval: (query) =>
      query.state.data?.status === PreflightRunStatus.RUNNING ? 2000 : false,
  });
}

/**
 * Subscribes to the workspace SSE stream and invalidates React Query caches
 * when a status change or audit event fires for this workspace.
 * Reconnects automatically with 5-second backoff on connection loss.
 */
export function useStreamWorkspaceEvents(
  workspaceId: string,
  accessToken: string | null | undefined,
) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!accessToken || !workspaceId) return;

    let cancelled = false;
    const abortController = new AbortController();

    async function listen() {
      while (!cancelled) {
        try {
          const t = createAuthApiTransport(accessToken!);
          const client = createClient(WorkspaceService, t);
          const request = create(StreamWorkspaceEventsRequestSchema, { workspaceId });

          for await (const event of client.streamWorkspaceEvents(request, {
            signal: abortController.signal,
          })) {
            if (cancelled) return;
            try {
              const p = event.payload ? (JSON.parse(event.payload) as { event_type?: string }) : {};
              if (p.event_type === 'keepalive') continue;
            } catch {
              // malformed payload — treat as a change signal
            }
            queryClient.invalidateQueries({ queryKey: ['workspaces', workspaceId] });
            queryClient.invalidateQueries({
              queryKey: ['audit', 'list', 'workspace', workspaceId],
            });
          }
        } catch (err) {
          if (cancelled || isNonRetriable(err)) return;
        }
        await streamSleep(5000, abortController.signal).catch(() => {});
      }
    }

    listen();
    return () => {
      cancelled = true;
      abortController.abort();
    };
  }, [workspaceId, accessToken, queryClient]);
}

export function useRetryWorkspaceProvisioning(token?: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string }) => {
      const client = createClient(WorkspaceService, transport(token));
      const request = create(RetryWorkspaceProvisioningRequestSchema, { id: input.id });
      return await client.retryWorkspaceProvisioning(request);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['workspaces', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['workspaces', 'list'] });
    },
  });
}
